import { Controller } from "@hotwired/stimulus"

// Client-side mirror of Art::IllustrationUploadGate (2:3 ±0.20%, quality tiers).
const TARGET_RATIO = 2 / 3
const ASPECT_TOLERANCE = 0.002
const MIN_SHORT_EDGE = 720
const TIER_1K_SHORT = 848
const TIER_2K_SHORT = 1696
const TIER_4K_SHORT = 3392
const PRO_2K_SIZE = "1696×2544"

function aspectOk(width, height) {
  if (width <= 0 || height <= 0) return false
  const ratio = width / height
  return Math.abs(ratio - TARGET_RATIO) / TARGET_RATIO <= ASPECT_TOLERANCE
}

function tierFor(width, height) {
  const short = Math.min(width, height)
  if (short < MIN_SHORT_EDGE) return "unusable"
  if (short < TIER_1K_SHORT) return "below_1k"
  if (short < TIER_2K_SHORT) return "1K"
  if (short < TIER_4K_SHORT) return "2K"
  return "4K"
}

export default class extends Controller {
  static targets = ["input", "status", "submit"]

  connect() {
    this.refresh()
  }

  refresh() {
    const file = this.hasInputTarget ? this.inputTarget.files?.[0] : null
    if (!file) {
      this.setStatus(
        `Choose a vertical 2:3 PNG, JPEG, or WebP. Quality is measured against Pro 2K (${PRO_2K_SIZE}).`,
        "muted"
      )
      this.setSubmitEnabled(false)
      return
    }

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      this.renderMeasure(img.naturalWidth, img.naturalHeight)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      this.setStatus("Could not read image dimensions.", "error")
      this.setSubmitEnabled(false)
    }
    img.src = url
  }

  renderMeasure(width, height) {
    const lines = [`Detected ${width}×${height}.`]
    let hardFail = false
    let warn = false

    if (!aspectOk(width, height)) {
      lines.push("Proportion ✗ — need vertical 2:3 (same as NanoBanana Pro).")
      hardFail = true
    } else {
      lines.push("Proportion ✓ 2:3")
    }

    const tier = tierFor(width, height)
    if (tier === "unusable") {
      lines.push(`Quality ✗ too small — minimum short edge ${MIN_SHORT_EDGE}px (e.g. 720×1080).`)
      hardFail = true
    } else if (tier === "2K" || tier === "4K") {
      lines.push(`Quality ✓ ${tier} — matches NanoBanana Pro 2K target (${PRO_2K_SIZE}).`)
    } else {
      lines.push(`Quality ⚠ ${tier} — below Pro 2K (${PRO_2K_SIZE}). Upload allowed.`)
      warn = true
    }

    const tone = hardFail ? "error" : warn ? "warn" : "ok"
    this.setStatus(lines.join(" "), tone)
    this.setSubmitEnabled(!hardFail)
  }

  setStatus(message, tone) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("cp-flash")
    this.statusTarget.classList.toggle("cp-flash-alert", tone === "error" || tone === "warn")
    this.statusTarget.classList.toggle("cp-flash-ok", tone === "ok")
  }

  setSubmitEnabled(enabled) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = !enabled
  }
}
